import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { entityFormKey } from "../../../../helpers/entityEditForm";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../helpers/money";
import {
  createPlan,
  CreatePlanPayload,
  deletePlan,
  getPlan,
  getPlans,
  Plan,
  PlanCurrency,
  updatePlan,
} from "../../../../services/planService";
import { PaginationMeta } from "../../../../types/api";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;

interface PlanFormValues {
  name: string;
  description: string;
  currency: PlanCurrency;
  base_price_display: string;
  max_units: number;
  max_professionals: number;
  extra_unit_price_display: string;
  extra_professional_price_display: string;
  is_active: boolean;
}

const emptyFormValues = (): PlanFormValues => ({
  name: "",
  description: "",
  currency: "BRL",
  base_price_display: "",
  max_units: 1,
  max_professionals: 1,
  extra_unit_price_display: "",
  extra_professional_price_display: "",
  is_active: true,
});

const centsToDisplay = (cents: number | null): string =>
  cents !== null && cents !== undefined ? formatCentsToMoneyInput(cents) : "";

const parseOptionalCents = (value: string): number | null => {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  return parseMoneyInputToCents(trimmed);
};

const mapPlanToFormValues = (plan: Plan): PlanFormValues => ({
  name: plan.name,
  description: plan.description,
  currency: plan.currency,
  base_price_display: formatCentsToMoneyInput(plan.base_price),
  max_units: plan.max_units,
  max_professionals: plan.max_professionals,
  extra_unit_price_display: centsToDisplay(plan.extra_unit_price),
  extra_professional_price_display: centsToDisplay(plan.extra_professional_price),
  is_active: plan.is_active,
});

const buildCreatePayload = (values: PlanFormValues): CreatePlanPayload => ({
  name: values.name.trim(),
  description: values.description.trim(),
  currency: values.currency,
  base_price: parseMoneyInputToCents(values.base_price_display),
  max_units: Number(values.max_units),
  max_professionals: Number(values.max_professionals),
  extra_unit_price: parseOptionalCents(values.extra_unit_price_display),
  extra_professional_price: parseOptionalCents(values.extra_professional_price_display),
  is_active: values.is_active,
});

const buildUpdatePayload = (
  values: PlanFormValues,
  isPriceLocked: boolean
): Partial<CreatePlanPayload> => {
  const base = {
    name: values.name.trim(),
    description: values.description.trim(),
    max_units: Number(values.max_units),
    max_professionals: Number(values.max_professionals),
    is_active: values.is_active,
  };

  if (isPriceLocked) {
    return base;
  }

  return {
    ...base,
    currency: values.currency,
    base_price: parseMoneyInputToCents(values.base_price_display),
    extra_unit_price: parseOptionalCents(values.extra_unit_price_display),
    extra_professional_price: parseOptionalCents(
      values.extra_professional_price_display
    ),
  };
};

const SuperAdminPlans = () => {
  const { t, i18n } = useTranslation();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState<"" | PlanCurrency>("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | "inactive">("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Plan | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const isPriceLocked = Boolean(
    editingPlan && editingPlan.subscriptions_count > 0
  );

  const formatMoney = useCallback(
    (cents: number, currency: PlanCurrency) =>
      formatMoneyFromCents(cents, i18n.language, currency),
    [i18n.language]
  );

  const validationSchema = useMemo(() => {
    const priceRequired = (messageKey: string) =>
      Yup.string()
        .required(t(messageKey))
        .test("price-valid", t("admin.plans.validation.priceInvalid"), (value) => {
          const parsed = parseMoneyInputToCents(value || "");
          return parsed >= 0;
        });

    const priceOptional = () =>
      Yup.string().test(
        "price-valid",
        t("admin.plans.validation.priceInvalid"),
        (value) => {
          if (!value || value.trim() === "") {
            return true;
          }

          const parsed = parseMoneyInputToCents(value);
          return parsed >= 0;
        }
      );

    return Yup.object({
      name: Yup.string().trim().required(t("admin.plans.validation.nameRequired")),
      description: Yup.string()
        .trim()
        .required(t("admin.plans.validation.descriptionRequired")),
      currency: isPriceLocked
        ? Yup.mixed<PlanCurrency>()
        : Yup.mixed<PlanCurrency>()
            .oneOf(["BRL", "USD"])
            .required(t("admin.plans.validation.currencyRequired")),
      base_price_display: isPriceLocked
        ? Yup.string()
        : priceRequired("admin.plans.validation.basePriceRequired"),
      max_units: Yup.number()
        .typeError(t("admin.plans.validation.maxUnitsType"))
        .integer(t("admin.plans.validation.maxUnitsInteger"))
        .min(1, t("admin.plans.validation.maxUnitsMin"))
        .required(t("admin.plans.validation.maxUnitsRequired")),
      max_professionals: Yup.number()
        .typeError(t("admin.plans.validation.maxProfessionalsType"))
        .integer(t("admin.plans.validation.maxProfessionalsInteger"))
        .min(1, t("admin.plans.validation.maxProfessionalsMin"))
        .required(t("admin.plans.validation.maxProfessionalsRequired")),
      extra_unit_price_display: isPriceLocked
        ? Yup.string()
        : priceOptional(),
      extra_professional_price_display: isPriceLocked
        ? Yup.string()
        : priceOptional(),
      is_active: Yup.boolean(),
    });
  }, [t, isPriceLocked]);

  useEffect(() => {
    document.title = t("admin.plans.pageTitle");
  }, [t, i18n.language]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, currencyFilter, statusFilter]);

  const fetchPlans = useCallback(async () => {
    setLoading(true);

    try {
      const response = await getPlans({
        page,
        per_page: PER_PAGE,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(currencyFilter ? { currency: currencyFilter } : {}),
        ...(statusFilter === "active" ? { is_active: true } : {}),
        ...(statusFilter === "inactive" ? { is_active: false } : {}),
      });

      setPlans(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("admin.plans.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, currencyFilter, statusFilter, t]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const formInitialValues = useMemo(
    () => (editingPlan ? mapPlanToFormValues(editingPlan) : emptyFormValues()),
    [editingPlan]
  );

  const formik = useFormik<PlanFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);

      try {
        if (editingPlan) {
          const response = await updatePlan(
            editingPlan.id,
            buildUpdatePayload(values, isPriceLocked)
          );
          toast.success(response.message);
        } else {
          const response = await createPlan(buildCreatePayload(values));
          toast.success(response.message);
        }

        closeOffcanvas();

        if (!editingPlan && page !== 1) {
          setPage(1);
        } else {
          await fetchPlans();
        }
      } catch (error: any) {
        toast.error(error?.data || t("admin.plans.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingPlan(null);
    setFormLoading(false);
  };

  const openCreate = () => {
    setEditingPlan(null);
    setFormLoading(false);
    setOffcanvasOpen(true);
  };

  const openEdit = async (plan: Plan) => {
    setEditingPlan(null);
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getPlan(plan.id);
      setEditingPlan(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("admin.plans.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      const response = await deletePlan(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);

      const isLastItemOnPage = plans.length === 1 && page > 1;

      if (isLastItemOnPage) {
        setPage((current) => current - 1);
      } else {
        await fetchPlans();
      }
    } catch (error: any) {
      toast.error(error?.data || t("admin.plans.toast.deleteError"));
    }
  };

  const totalPages = meta?.last_page ?? 1;
  const hasActiveSearch = debouncedSearch.length > 0;
  const hasActiveFilters =
    hasActiveSearch || currencyFilter !== "" || statusFilter !== "";

  return (
    <React.Fragment>
      <ToastContainer closeButton pauseOnHover />
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("admin.plans.breadcrumb.title")}
            pageTitle={t("admin.plans.breadcrumb.pageTitle")}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                  <h4 className="card-title mb-0 flex-grow-1">
                    {t("admin.plans.card.title")}
                  </h4>
                  <Button color="primary" onClick={openCreate}>
                    <i className="ri-add-line align-bottom me-1" />
                    {t("admin.plans.actions.new")}
                  </Button>
                </CardHeader>

                <CardBody>
                  <Row className="mb-3 g-3">
                    <Col md={4} lg={3}>
                      <div className="search-box">
                        <Input
                          type="text"
                          className="form-control"
                          placeholder={t("admin.plans.search.placeholder")}
                          value={searchTerm}
                          onChange={(event) => setSearchTerm(event.target.value)}
                        />
                        <i className="ri-search-line search-icon" />
                      </div>
                    </Col>
                    <Col md={4} lg={2}>
                      <Input
                        type="select"
                        value={currencyFilter}
                        onChange={(event) =>
                          setCurrencyFilter(event.target.value as "" | PlanCurrency)
                        }
                      >
                        <option value="">{t("admin.plans.filters.currency.all")}</option>
                        <option value="BRL">{t("admin.plans.filters.currency.brl")}</option>
                        <option value="USD">{t("admin.plans.filters.currency.usd")}</option>
                      </Input>
                    </Col>
                    <Col md={4} lg={2}>
                      <Input
                        type="select"
                        value={statusFilter}
                        onChange={(event) =>
                          setStatusFilter(
                            event.target.value as "" | "active" | "inactive"
                          )
                        }
                      >
                        <option value="">{t("admin.plans.filters.status.all")}</option>
                        <option value="active">
                          {t("admin.plans.filters.status.active")}
                        </option>
                        <option value="inactive">
                          {t("admin.plans.filters.status.inactive")}
                        </option>
                      </Input>
                    </Col>
                  </Row>

                  {loading ? (
                    <div className="d-flex justify-content-center py-5">
                      <Spinner color="primary" />
                    </div>
                  ) : plans.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p className="mb-3">
                        {hasActiveFilters
                          ? t("admin.plans.empty.noResults")
                          : t("admin.plans.empty.noData")}
                      </p>
                      {!hasActiveFilters && (
                        <Button color="soft-primary" onClick={openCreate}>
                          {t("admin.plans.actions.createFirst")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>{t("admin.plans.table.name")}</th>
                              <th>{t("admin.plans.table.currency")}</th>
                              <th>{t("admin.plans.table.basePrice")}</th>
                              <th>{t("admin.plans.table.limits")}</th>
                              <th>{t("admin.plans.table.subscribers")}</th>
                              <th>{t("admin.plans.table.status")}</th>
                              <th className="text-end" style={{ width: 120 }}>
                                {t("admin.plans.table.actions")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {plans.map((plan) => (
                              <tr key={plan.id}>
                                <td>
                                  <div className="fw-medium">{plan.name}</div>
                                  <div className="text-muted small text-truncate" style={{ maxWidth: 280 }}>
                                    {plan.description}
                                  </div>
                                </td>
                                <td>
                                  <Badge color="soft-secondary" className="fs-12">
                                    {plan.currency}
                                  </Badge>
                                </td>
                                <td>{formatMoney(plan.base_price, plan.currency)}</td>
                                <td className="text-muted">
                                  {t("admin.plans.table.limitsValue", {
                                    units: plan.max_units,
                                    professionals: plan.max_professionals,
                                  })}
                                </td>
                                <td>
                                  <Badge
                                    color={
                                      plan.subscriptions_count > 0
                                        ? "soft-warning"
                                        : "soft-secondary"
                                    }
                                    className="fs-12"
                                  >
                                    {plan.subscriptions_count}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge
                                    color={plan.is_active ? "success" : "secondary"}
                                    className="fs-12"
                                  >
                                    {plan.is_active
                                      ? t("admin.plans.status.active")
                                      : t("admin.plans.status.inactive")}
                                  </Badge>
                                </td>
                                <td className="text-end">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    className="me-1"
                                    title={t("admin.plans.actions.edit")}
                                    onClick={() => openEdit(plan)}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    title={t("admin.plans.actions.delete")}
                                    onClick={() => {
                                      setDeleteTarget(plan);
                                      setDeleteModal(true);
                                    }}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>

                      {meta && totalPages > 1 && (
                        <Row className="align-items-center mt-3 g-3">
                          <Col sm={6}>
                            <p className="text-muted mb-0">
                              {t("admin.plans.pagination.showing", {
                                from: meta.from ?? 0,
                                to: meta.to ?? 0,
                                total: meta.total,
                              })}
                            </p>
                          </Col>
                          <Col sm={6}>
                            <Pagination className="pagination-separated justify-content-sm-end mb-0">
                              <PaginationItem disabled={page <= 1}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page > 1) {
                                      setPage(page - 1);
                                    }
                                  }}
                                >
                                  {t("admin.plans.pagination.previous")}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem active>
                                <PaginationLink href="#">
                                  {page} / {totalPages}
                                </PaginationLink>
                              </PaginationItem>
                              <PaginationItem disabled={page >= totalPages}>
                                <PaginationLink
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    if (page < totalPages) {
                                      setPage(page + 1);
                                    }
                                  }}
                                >
                                  {t("admin.plans.pagination.next")}
                                </PaginationLink>
                              </PaginationItem>
                            </Pagination>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <Offcanvas
        isOpen={offcanvasOpen}
        toggle={closeOffcanvas}
        direction="end"
        className="border-0"
        style={{ width: 480 }}
      >
        <OffcanvasHeader toggle={closeOffcanvas} className="bg-light">
          {editingPlan
            ? t("admin.plans.form.editTitle")
            : t("admin.plans.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <Form
            key={entityFormKey(editingPlan?.id)}
            onSubmit={(event) => {
              event.preventDefault();
              formik.handleSubmit();
            }}
          >
            <h6 className="text-muted text-uppercase fs-12 mb-3">
              {t("admin.plans.form.section.details")}
            </h6>

            <div className="mb-3">
              <Label htmlFor="plan-name">{t("admin.plans.fields.name.label")}</Label>
              <Input
                id="plan-name"
                name="name"
                placeholder={t("admin.plans.fields.name.placeholder")}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.name && formik.errors.name)}
              />
              {formik.touched.name && formik.errors.name ? (
                <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
              ) : null}
            </div>

            <div className="mb-3">
              <Label htmlFor="plan-description">
                {t("admin.plans.fields.description.label")}
              </Label>
              <Input
                id="plan-description"
                name="description"
                type="textarea"
                rows={3}
                placeholder={t("admin.plans.fields.description.placeholder")}
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.description && formik.errors.description)}
              />
              {formik.touched.description && formik.errors.description ? (
                <FormFeedback type="invalid">{formik.errors.description}</FormFeedback>
              ) : null}
            </div>

            <div className="form-check form-switch form-switch-md mb-4">
              <Input
                id="plan-is-active"
                name="is_active"
                type="checkbox"
                role="switch"
                className="form-check-input"
                checked={formik.values.is_active}
                onChange={formik.handleChange}
              />
              <Label className="form-check-label" htmlFor="plan-is-active">
                {t("admin.plans.fields.isActive.label")}
              </Label>
            </div>

            <h6 className="text-muted text-uppercase fs-12 mb-3">
              {t("admin.plans.form.section.pricing")}
            </h6>

            {isPriceLocked ? (
              <Alert color="warning" className="fs-13">
                {t("admin.plans.hints.priceImmutable")}
              </Alert>
            ) : null}

            <Row className="g-3 mb-3">
              <Col md={4}>
                <Label htmlFor="plan-currency">
                  {t("admin.plans.fields.currency.label")}
                </Label>
                <Input
                  id="plan-currency"
                  name="currency"
                  type="select"
                  value={formik.values.currency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isPriceLocked}
                  invalid={!!(formik.touched.currency && formik.errors.currency)}
                >
                  <option value="BRL">{t("admin.plans.filters.currency.brl")}</option>
                  <option value="USD">{t("admin.plans.filters.currency.usd")}</option>
                </Input>
                {formik.touched.currency && formik.errors.currency ? (
                  <FormFeedback type="invalid">{formik.errors.currency}</FormFeedback>
                ) : null}
              </Col>
              <Col md={8}>
                <Label htmlFor="plan-base-price">
                  {t("admin.plans.fields.basePrice.label")}
                </Label>
                <Input
                  id="plan-base-price"
                  name="base_price_display"
                  placeholder={t("admin.plans.fields.basePrice.placeholder")}
                  value={formik.values.base_price_display}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isPriceLocked}
                  invalid={
                    !!(formik.touched.base_price_display && formik.errors.base_price_display)
                  }
                />
                {formik.touched.base_price_display && formik.errors.base_price_display ? (
                  <FormFeedback type="invalid">
                    {formik.errors.base_price_display}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <Row className="g-3 mb-4">
              <Col md={6}>
                <Label htmlFor="plan-extra-unit">
                  {t("admin.plans.fields.extraUnitPrice.label")}
                </Label>
                <Input
                  id="plan-extra-unit"
                  name="extra_unit_price_display"
                  placeholder={t("admin.plans.fields.extraUnitPrice.placeholder")}
                  value={formik.values.extra_unit_price_display}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isPriceLocked}
                  invalid={
                    !!(
                      formik.touched.extra_unit_price_display &&
                      formik.errors.extra_unit_price_display
                    )
                  }
                />
                {formik.touched.extra_unit_price_display &&
                formik.errors.extra_unit_price_display ? (
                  <FormFeedback type="invalid">
                    {formik.errors.extra_unit_price_display}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="plan-extra-professional">
                  {t("admin.plans.fields.extraProfessionalPrice.label")}
                </Label>
                <Input
                  id="plan-extra-professional"
                  name="extra_professional_price_display"
                  placeholder={t(
                    "admin.plans.fields.extraProfessionalPrice.placeholder"
                  )}
                  value={formik.values.extra_professional_price_display}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={isPriceLocked}
                  invalid={
                    !!(
                      formik.touched.extra_professional_price_display &&
                      formik.errors.extra_professional_price_display
                    )
                  }
                />
                {formik.touched.extra_professional_price_display &&
                formik.errors.extra_professional_price_display ? (
                  <FormFeedback type="invalid">
                    {formik.errors.extra_professional_price_display}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <h6 className="text-muted text-uppercase fs-12 mb-3">
              {t("admin.plans.form.section.limits")}
            </h6>

            <Row className="g-3 mb-4">
              <Col md={6}>
                <Label htmlFor="plan-max-units">
                  {t("admin.plans.fields.maxUnits.label")}
                </Label>
                <Input
                  id="plan-max-units"
                  name="max_units"
                  type="number"
                  min={1}
                  value={formik.values.max_units}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.max_units && formik.errors.max_units)}
                />
                {formik.touched.max_units && formik.errors.max_units ? (
                  <FormFeedback type="invalid">{formik.errors.max_units}</FormFeedback>
                ) : null}
              </Col>
              <Col md={6}>
                <Label htmlFor="plan-max-professionals">
                  {t("admin.plans.fields.maxProfessionals.label")}
                </Label>
                <Input
                  id="plan-max-professionals"
                  name="max_professionals"
                  type="number"
                  min={1}
                  value={formik.values.max_professionals}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.max_professionals && formik.errors.max_professionals)
                  }
                />
                {formik.touched.max_professionals && formik.errors.max_professionals ? (
                  <FormFeedback type="invalid">
                    {formik.errors.max_professionals}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button color="light" type="button" onClick={closeOffcanvas}>
                {t("admin.plans.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <Spinner size="sm" className="me-1" />
                ) : (
                  <i className="ri-save-line align-bottom me-1" />
                )}
                {t("admin.plans.actions.save")}
              </Button>
            </div>
          </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <i className="ri-delete-bin-line display-5 text-danger" />
            <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
              <h4>{t("admin.plans.delete.title")}</h4>
              <p className="text-muted mx-4 mb-0">
                {deleteTarget
                  ? deleteTarget.subscriptions_count > 0
                    ? t("admin.plans.delete.descriptionWithSubscribers", {
                        name: deleteTarget.name,
                      })
                    : t("admin.plans.delete.description", { name: deleteTarget.name })
                  : t("admin.plans.delete.fallback")}
              </p>
            </div>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4 mb-2">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("admin.plans.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete}>
              {t("admin.plans.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default SuperAdminPlans;
